"use client";
import NamePlateComponent from "@/components/dashboard/general/svgs/NamePlateComponent";
import OtherPlayerRunestonesCabinet from "@/components/dashboard/profile/OtherPlayerRunestonesCabinet";
import OtherPlayerShieldTagsCabinet from "@/components/dashboard/profile/OtherPlayerShieldTagsCabinet";
import OtherShieldTwoTagsCabinet from "@/components/dashboard/profile/OtherShieldTwoTagsCabinet";
import CircularLoader from "@/components/loading/LoadingComponent";
import {
    OTHER_PLAYER_QUERY,
    OTHER_PLAYER_STATS_QUERY,
} from "@/graphql/queries/profileQueries";
import { formatNumber } from "@/helpers/common/formatNumber";
import { convertLetters } from "@/utils/letterConversion";
import { gql, useQuery } from "@apollo/client";
import { useState } from "react";
import { useParams } from "react-router-dom";

const FEATURE_FLAGS_QUERY = gql`
    query FeatureFlagsForOtherProfile {
        featureFlags {
            streamTeamChannelPointRewardEnabled
        }
    }
`;

export default function PlayerProfilePage() {
    const { player_id } = useParams<{ player_id: string }>();

    const [activeTab, setActiveTab] = useState(0);
    const [selectedVisual, setSelectedVisual] = useState<
        "shield1" | "shield2" | "avatar"
    >("shield1");

    const {
        data: playerData,
        loading: playerLoading,
        error: playerError,
    } = useQuery(OTHER_PLAYER_QUERY, {
        variables: { playerId: player_id },
        skip: !player_id,
    });

    const { data, loading, error } = useQuery(OTHER_PLAYER_STATS_QUERY, {
        variables: { playerId: player_id },
        skip: !player_id,
        fetchPolicy: "cache-and-network",
    });

    const { data: featureFlagsData } = useQuery(FEATURE_FLAGS_QUERY);
    const isStreamTeamRewardFeatureEnabled = Boolean(
        featureFlagsData?.featureFlags?.streamTeamChannelPointRewardEnabled,
    );

    // Note: Since OTHER_PLAYER_STATS is player-specific and we don't have
    // a subscription for other players yet, we keep this as query-only.
    // In the future, consider adding an otherPlayerStats subscription
    // if real-time updates for viewing other players' profiles are needed.

    if (loading || playerLoading) {
        return (
            <div className="flex min-h-screen w-full items-center justify-center">
                <CircularLoader />
            </div>
        );
    }
    if (error || playerError) {
        console.error("Error fetching player data:", error || playerError);
        // Fallback to using the playerData if available
        return (
            <div className="flex min-h-screen w-full items-center justify-center">
                <div className="text-center">
                    <h1 className="text-2xl font-bold text-red-500">
                        Error Loading Player Data
                    </h1>
                    <p className="mt-2 text-gray-600">
                        There was an error loading the player data. Please try
                        again later.
                    </p>
                </div>
            </div>
        );
    }

    const player = playerData?.otherPlayer;

    const shield = player?.shield;
    const shieldTwo = player?.shieldTwo || null;
    const avatarUrl = player?.avatarUrl || null;

    const hasShield2 = shieldTwo?.imageWithTagsUrl || shieldTwo?.imageUrl;
    const hasAvatar = !!avatarUrl;
    const shouldShowVisualTabs = hasShield2 || hasAvatar;

    const playerName = player?.name || "";
    const clanBorderColor = player?.clan?.borderColor?.hex || "#ffcc33";
    const clanRankColor = player?.clan?.rankColor?.hex || "#ffcc33";
    const clanRankNumberColor = player?.clan?.rankNumberColor?.hex || "#FDFABB";
    const overallMonthsSubscribed = Math.floor(
        (player?.overallSubTimeInDays ?? 0) / 30,
    );
    const overallMonthsInGota = Math.floor(
        (player?.gotaSubTimeInDays ?? 0) / 30,
    );

    const renderSelectedImage = (): string | undefined => {
        if (selectedVisual === "shield2") {
            return (
                shieldTwo?.imageWithTagsUrl ?? shieldTwo?.imageUrl ?? undefined
            );
        }
        if (selectedVisual === "avatar") {
            return avatarUrl ?? undefined;
        }
        return shield!.imageWithTagsUrl ?? shield!.imageUrl ?? undefined;
    };

    const totalStats = [
        {
            name: "Shield Value",
            stat: formatNumber(data?.otherPlayerStats?.shieldValue),
        },
        ...(hasShield2
            ? [
                  {
                      name: "Shield 2 Value",
                      stat: formatNumber(
                          data?.otherPlayerStats?.shieldTwoValue,
                      ),
                  },
              ]
            : []),
        {
            name: "Lifetime VP Rank",
            stat: formatNumber(data?.otherPlayerStats?.lifetimeRank),
        },
        {
            name: "Season Rank",
            stat: formatNumber(data?.otherPlayerStats?.overallRank),
        },
        {
            name: player?.clan?.tag + " Rank",
            stat: formatNumber(data?.otherPlayerStats?.clanRank),
        },
    ];

    const stats = [
        {
            title: "Lifetime VP",
            value: formatNumber(data?.otherPlayerStats?.lifetimeVp || 0),
            color: "border-brand",
        },
        {
            title: "Season VP",
            value: formatNumber(data?.otherPlayerStats?.seasonVp || 0),
            color: "border-brand",
        },
        {
            title: "Checkins",
            value: formatNumber(data?.otherPlayerStats?.checkins || 0),
            color: "border-checkins",
        },
        {
            title: "Raids",
            value: formatNumber(data?.otherPlayerStats?.raids || 0),
            color: "border-raids",
        },
        {
            title: "Sentry Time",
            value: `${formatNumber(Math.floor(data?.otherPlayerStats?.sentryTime / 60))} hours`,
            color: "border-sentry",
        },
        {
            title: "Bits",
            value: formatNumber(data?.otherPlayerStats?.bits || 0),
            color: "border-rainbow",
        },
        {
            title: "Gifted Subs",
            value: formatNumber(data?.otherPlayerStats?.giftedSubs || 0),
            color: "border-gifted",
        },
        {
            title: "Wheel of Hamingja",
            value: formatNumber(
                data?.otherPlayerStats?.wheelOfHamingjaWins || 0,
            ),
            color: "border-hamingja",
        },
        {
            title: "Exalted",
            value: formatNumber(data?.otherPlayerStats?.exalted || 0),
            color: "border-exalted",
        },
        {
            title: "Reforge Tokens",
            value: formatNumber(data?.otherPlayerStats?.reforgeTokens || 0),
            color: "border-exalted",
        },
        {
            title: "WOH Tokens",
            value: formatNumber(data?.otherPlayerStats?.wohTokens || 0),
            color: "border-hamingja",
        },
        ...(isStreamTeamRewardFeatureEnabled
            ? [
                  {
                      title: "Ravenguard Points",
                      value: formatNumber(
                          data?.otherPlayerStats?.ravenguardPoints || 0,
                      ),
                      color: "border-brand",
                  },
              ]
            : []),
        {
            title: "Shield Mates",
            value: formatNumber(data?.otherPlayerStats?.shieldMatesCount || 0),
            color: "border-brand",
        },
        {
            title: "Months Subscribed",
            value: formatNumber(overallMonthsSubscribed),
            color: "border-brand",
        },
    ];

    if (overallMonthsInGota > 0) {
        stats.push({
            title: "Months in Gota",
            value: formatNumber(overallMonthsInGota),
            color: "border-brand",
        });
    }

    // Shield 2 stats (progress since Shield 2 creation)
    const shieldTwoStats = hasShield2
        ? [
              {
                  title: "Shield 2 VP",
                  value: formatNumber(data?.otherPlayerStats?.shieldTwoVp || 0),
                  color: "border-brand",
              },
              {
                  title: "Checkins",
                  value: formatNumber(
                      data?.otherPlayerStats?.shieldTwoCheckins || 0,
                  ),
                  color: "border-checkins",
              },
              {
                  title: "Raids",
                  value: formatNumber(
                      data?.otherPlayerStats?.shieldTwoRaids || 0,
                  ),
                  color: "border-raids",
              },
              {
                  title: "Sentry Time",
                  value: `${formatNumber(Math.floor((data?.otherPlayerStats?.shieldTwoSentryTime || 0) / 60))} hours`,
                  color: "border-sentry",
              },
              {
                  title: "Bits",
                  value: formatNumber(
                      data?.otherPlayerStats?.shieldTwoBits || 0,
                  ),
                  color: "border-rainbow",
              },
              {
                  title: "Gifted Subs",
                  value: formatNumber(
                      data?.otherPlayerStats?.shieldTwoGiftedSubs || 0,
                  ),
                  color: "border-gifted",
              },
              {
                  title: "Wheel of Hamingja",
                  value: formatNumber(
                      data?.otherPlayerStats?.shieldTwoWheelOfHamingjaWins || 0,
                  ),
                  color: "border-hamingja",
              },
              ...(isStreamTeamRewardFeatureEnabled
                  ? [
                        {
                            title: "Ravenguard Points",
                            value: formatNumber(
                                data?.otherPlayerStats
                                    ?.shieldTwoRavenguardPoints || 0,
                            ),
                            color: "border-brand",
                        },
                    ]
                  : []),
              {
                  title: "Total Donated",
                  value:
                      "$" +
                      formatNumber(
                          data?.otherPlayerStats?.shieldTwoTotalDonated || 0,
                      ) +
                      " AUD",
                  color: "border-brand",
              },
              {
                  title: "Merchandise",
                  value:
                      "$" +
                      formatNumber(
                          data?.otherPlayerStats?.shieldTwoMerchandise || 0,
                      ) +
                      " AUD",
                  color: "border-brand",
              },
              {
                  title: "Months Subscribed",
                  value: formatNumber(
                      data?.otherPlayerStats?.shieldTwoMonthsSubscribed || 0,
                  ),
                  color: "border-brand",
              },
              {
                  title: "Months in Gota",
                  value: formatNumber(
                      data?.otherPlayerStats?.shieldTwoGotaMonths || 0,
                  ),
                  color: "border-brand",
              },
          ]
        : [];

    // Determine whether to show Shield 2 context
    const showShieldTwo = selectedVisual === "shield2" && hasShield2;
    const displayStats = showShieldTwo ? shieldTwoStats : stats;

    const tabs = [
        {
            title: "Shield Tags",
            content: <OtherPlayerShieldTagsCabinet playerId={player.id} />,
            enabled: true,
        },
        ...(hasShield2
            ? [
                  {
                      title: "Shield 2 Tags",
                      content: (
                          <OtherShieldTwoTagsCabinet playerId={player.id} />
                      ),
                      enabled: true,
                  },
              ]
            : []),
        {
            title: "Runestones",
            content: <OtherPlayerRunestonesCabinet playerId={player.id} />,
            enabled: true,
        },
        {
            title: "History",
            content: <div>Content for History</div>,
            enabled: false,
        },
        { title: "YGGY", content: <div>Content for YGGY</div>, enabled: false },
    ];

    // Find the Shield 2 Tags tab index for syncing visual selection
    const shieldTwoTabIndex = tabs.findIndex(
        (tab) => tab.title === "Shield 2 Tags",
    );
    const shieldOneTabIndex = tabs.findIndex(
        (tab) => tab.title === "Shield Tags",
    );

    const handleTabChange = (index: number) => {
        setActiveTab(index);
        if (index === shieldTwoTabIndex && shieldTwoTabIndex !== -1) {
            setSelectedVisual("shield2");
        } else if (index === shieldOneTabIndex) {
            setSelectedVisual("shield1");
        }
    };

    return (
        <div className="flex min-h-screen w-full flex-col items-center p-4">
            <div className="w-full">
                <div className="grid grid-cols-1 gap-4 xl:grid-cols-4">
                    {/* Left column */}
                    <div className="border-brand flex flex-col border-4 bg-black/70 pb-4 md:col-span-1">
                        <div className="relative">
                            <img
                                src={renderSelectedImage()}
                                alt="Profile"
                                className={`border-b-brand w-full border-b-4 object-cover ${
                                    selectedVisual !== "avatar" ? "p-4" : ""
                                }`}
                                style={{
                                    backgroundImage: `url(/wallhalla-blank-wall.png)`,
                                }}
                            />

                            <NamePlateComponent
                                text={playerName}
                                className="absolute bottom-2 left-1/2 z-10 max-w-full -translate-x-1/2 transform"
                            />
                        </div>

                        {shouldShowVisualTabs && (
                            <div className="flex justify-center space-x-2 py-2">
                                <button
                                    onClick={() => {
                                        setSelectedVisual("shield1");
                                        if (shieldOneTabIndex !== -1)
                                            setActiveTab(shieldOneTabIndex);
                                    }}
                                    className={`font-prviking rounded px-3 py-1 text-sm sm:text-base ${
                                        selectedVisual === "shield1"
                                            ? "bg-brand text-brand_charcoal"
                                            : "hover:bg-brand hover:text-brand_charcoal bg-gray-800 text-white"
                                    }`}
                                >
                                    {convertLetters("Shield 1")}
                                </button>
                                {hasShield2 && (
                                    <button
                                        onClick={() => {
                                            setSelectedVisual("shield2");
                                            if (shieldTwoTabIndex !== -1)
                                                setActiveTab(shieldTwoTabIndex);
                                        }}
                                        className={`font-prviking rounded px-3 py-1 text-sm sm:text-base ${
                                            selectedVisual === "shield2"
                                                ? "bg-brand text-brand_charcoal"
                                                : "hover:bg-brand hover:text-brand_charcoal bg-gray-800 text-white"
                                        }`}
                                    >
                                        {convertLetters("Shield 2")}
                                    </button>
                                )}
                                {hasAvatar && (
                                    <button
                                        onClick={() =>
                                            setSelectedVisual("avatar")
                                        }
                                        className={`font-prviking rounded px-3 py-1 text-sm sm:text-base ${
                                            selectedVisual === "avatar"
                                                ? "bg-brand text-brand_charcoal"
                                                : "hover:bg-brand hover:text-brand_charcoal bg-gray-800 text-white"
                                        }`}
                                    >
                                        {convertLetters("Avatar")}
                                    </button>
                                )}
                            </div>
                        )}

                        <div className="mt-5 grid grid-cols-2 gap-2 px-2 sm:grid-cols-3 sm:px-4">
                            {displayStats.map((stat, index) => (
                                <div
                                    key={index}
                                    className={`rounded-lg border-4 bg-black/70 px-2 py-2 text-center text-xs sm:text-sm ${stat.color}`}
                                >
                                    <dt className="text-brand font-prviking wrap-break-word">
                                        {convertLetters(stat.title)}
                                    </dt>
                                    <dd className="text-stats font-garamond mt-1 text-lg wrap-break-word">
                                        {stat.value}
                                    </dd>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Right column */}
                    <div className="flex w-full flex-col md:col-span-3">
                        <dl
                            className={`grid grid-cols-2 gap-4 pb-4 sm:grid-cols-2 ${hasShield2 ? "md:grid-cols-5" : "md:grid-cols-4"}`}
                        >
                            {totalStats.map((item, index) => {
                                const isLast = index === totalStats.length - 1;
                                return (
                                    <div
                                        key={item.name}
                                        className={`overflow-hidden rounded-lg border-4 bg-black/80 px-4 py-5 shadow-sm ${
                                            !isLast ? "border-brand" : ""
                                        }`}
                                        style={
                                            isLast
                                                ? {
                                                      borderColor:
                                                          clanBorderColor,
                                                  }
                                                : {}
                                        }
                                    >
                                        <dt
                                            className={`font-prviking truncate text-center text-lg sm:text-2xl ${
                                                !isLast ? "text-brand" : ""
                                            }`}
                                            style={
                                                isLast
                                                    ? { color: clanRankColor }
                                                    : {}
                                            }
                                        >
                                            {convertLetters(item.name)}
                                        </dt>
                                        <dd
                                            className={`mt-1 text-center text-lg sm:text-2xl ${
                                                !isLast
                                                    ? "text-stats font-garamond"
                                                    : ""
                                            }`}
                                            style={
                                                isLast
                                                    ? {
                                                          color: clanRankNumberColor,
                                                      }
                                                    : {}
                                            }
                                        >
                                            {item.stat}
                                        </dd>
                                    </div>
                                );
                            })}
                        </dl>

                        <div className="border-brand w-full grow border-4 bg-black/70">
                            <div className="mb-4 overflow-x-auto">
                                <nav className="border-b-brand flex w-full overflow-x-auto border-b-4 whitespace-nowrap">
                                    {tabs.map((tab, index) => {
                                        const isDisabled = !tab.enabled;
                                        const borderRightClass =
                                            index < tabs.length - 1
                                                ? "border-r-4 border-r-brand"
                                                : "";

                                        return (
                                            <button
                                                key={index}
                                                onClick={() =>
                                                    !isDisabled &&
                                                    handleTabChange(index)
                                                }
                                                disabled={isDisabled}
                                                className={`font-prviking min-w-25 shrink-0 px-3 py-2 text-center text-sm leading-tight wrap-break-word sm:min-w-30 sm:text-base md:min-w-0 md:flex-1 md:text-2xl ${activeTab === index ? "bg-brand text-brand_charcoal" : ""} ${!isDisabled && activeTab !== index ? "text-brand hover:bg-brand hover:text-brand_charcoal" : ""} ${isDisabled ? "cursor-not-allowed text-gray-400 opacity-50" : ""} ${borderRightClass}`}
                                            >
                                                {convertLetters(tab.title)}
                                            </button>
                                        );
                                    })}
                                </nav>
                            </div>

                            <div className="px-2 sm:px-4">
                                {tabs[activeTab].content}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
